/**
 * Upload allegati task con pool di concorrenza e validazione allineata a TaskAttachment.
 */

var MAX_FILE_SIZE = 50 * 1024 * 1024;

var ALLOWED_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'odt', 'rtf',
    'xls', 'xlsx', 'ods', 'csv', 'tsv',
    'ppt', 'pptx', 'odp',
    'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg',
    'mp4', 'mov', 'avi', 'mkv', 'webm',
    'txt', 'md', 'json', 'xml', 'yml', 'yaml',
    'zip', 'rar', '7z', 'tar', 'gz', 'tgz',
];

function getExtension(fileName) {
    if (!fileName || fileName.indexOf('.') === -1) {
        return '';
    }

    return fileName.split('.').pop().toLowerCase();
}

function validateTaskAttachmentFile(file) {
    if (!file) {
        return 'File non valido';
    }

    if (file.size > MAX_FILE_SIZE) {
        return 'Il file supera il limite di 50MB';
    }

    var ext = getExtension(file.name);
    if (ext && ALLOWED_EXTENSIONS.indexOf(ext) === -1) {
        return 'Formato file non supportato';
    }

    return null;
}

function filesFromFileList(fileList) {
    if (!fileList || !fileList.length) {
        return [];
    }

    return Array.from(fileList);
}

function uploadSingleFile(taskId, file) {
    var axios = window.axios;
    if (!axios) {
        return Promise.reject(new Error('Client HTTP non disponibile'));
    }

    var formData = new FormData();
    formData.append('file', file);

    return axios.post('/tasks/' + taskId + '/attachments', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    }).then(function(response) {
        if (response.data && response.data.success && response.data.attachment) {
            return response.data.attachment;
        }

        throw new Error((response.data && response.data.message) || 'Errore durante il caricamento');
    }).catch(function(error) {
        var message = (error.response && error.response.data && error.response.data.message)
            || (error.response && error.response.data && error.response.data.errors && error.response.data.errors.file && error.response.data.errors.file[0])
            || error.message
            || 'Errore durante il caricamento';

        throw new Error(message);
    });
}

/**
 * @param {Object} options
 * @param {number|string} options.taskId
 * @param {File[]|FileList} options.files
 * @param {number} [options.concurrency=3]
 * @param {function} [options.onProgress]
 * @returns {Promise<{succeeded: Array, failed: Array}>}
 */
function uploadTaskAttachments(options) {
    var taskId = options.taskId;
    var concurrency = options.concurrency || 3;
    var onProgress = options.onProgress;
    var fileArray = filesFromFileList(options.files);

    var result = {
        succeeded: [],
        failed: [],
    };

    if (!taskId || fileArray.length === 0) {
        return Promise.resolve(result);
    }

    var queue = [];

    fileArray.forEach(function(file, index) {
        var validationError = validateTaskAttachmentFile(file);
        if (validationError) {
            result.failed.push({
                file: file,
                message: validationError,
                index: index,
            });
        } else {
            queue.push({ file: file, index: index });
        }
    });

    var total = fileArray.length;
    var done = result.failed.length;

    function reportProgress() {
        if (onProgress) {
            onProgress({
                done: done,
                total: total,
                active: done < total,
                errors: result.failed,
            });
        }
    }

    if (queue.length === 0) {
        reportProgress();

        return Promise.resolve(result);
    }

    reportProgress();

    var workerCount = Math.min(concurrency, queue.length);
    var items = queue.slice();

    function runWorker() {
        var next = items.shift();
        if (!next) {
            return Promise.resolve();
        }

        return uploadSingleFile(taskId, next.file)
            .then(function(attachment) {
                result.succeeded.push({
                    file: next.file,
                    attachment: attachment,
                    index: next.index,
                });
            })
            .catch(function(error) {
                result.failed.push({
                    file: next.file,
                    message: error.message || 'Errore durante il caricamento',
                    index: next.index,
                });
            })
            .then(function() {
                done += 1;
                reportProgress();

                return runWorker();
            });
    }

    var workers = [];
    for (var i = 0; i < workerCount; i++) {
        workers.push(runWorker());
    }

    return Promise.all(workers).then(function() {
        result.succeeded.sort(function(a, b) {
            return a.index - b.index;
        });
        result.failed.sort(function(a, b) {
            return a.index - b.index;
        });

        reportProgress();

        return result;
    });
}

/**
 * Upload sequenziale (ordine preservato) — utile per inserimento inline nell'editor.
 */
function uploadTaskAttachmentsSequential(options) {
    var taskId = options.taskId;
    var onProgress = options.onProgress;
    var fileArray = filesFromFileList(options.files);

    var result = {
        succeeded: [],
        failed: [],
    };

    if (!taskId || fileArray.length === 0) {
        return Promise.resolve(result);
    }

    var total = fileArray.length;
    var done = 0;

    function reportProgress() {
        if (onProgress) {
            onProgress({
                done: done,
                total: total,
                active: done < total,
                errors: result.failed,
            });
        }
    }

    reportProgress();

    var chain = Promise.resolve();

    fileArray.forEach(function(file, index) {
        chain = chain.then(function() {
            var validationError = validateTaskAttachmentFile(file);
            if (validationError) {
                result.failed.push({
                    file: file,
                    message: validationError,
                    index: index,
                });
                done += 1;
                reportProgress();

                return null;
            }

            return uploadSingleFile(taskId, file)
                .then(function(attachment) {
                    result.succeeded.push({
                        file: file,
                        attachment: attachment,
                        index: index,
                    });
                })
                .catch(function(error) {
                    result.failed.push({
                        file: file,
                        message: error.message || 'Errore durante il caricamento',
                        index: index,
                    });
                })
                .then(function() {
                    done += 1;
                    reportProgress();
                });
        });
    });

    return chain.then(function() {
        return result;
    });
}

function uploadSingleTaskAttachment(taskId, file) {
    var validationError = validateTaskAttachmentFile(file);
    if (validationError) {
        return Promise.reject(new Error(validationError));
    }

    return uploadSingleFile(taskId, file);
}

module.exports = {
    validateTaskAttachmentFile: validateTaskAttachmentFile,
    filesFromFileList: filesFromFileList,
    uploadTaskAttachments: uploadTaskAttachments,
    uploadTaskAttachmentsSequential: uploadTaskAttachmentsSequential,
    uploadSingleTaskAttachment: uploadSingleTaskAttachment,
};
